import { useState, useEffect, useRef } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Layout as AntLayout, Drawer, Menu, Badge } from 'antd';
import {
  MenuOutlined,
  DashboardOutlined,
  LineChartOutlined,
  BarChartOutlined,
  SearchOutlined,
  BellOutlined,
  BookOutlined,
  PlaySquareOutlined,
  FundOutlined,
  BulbOutlined,
  BulbFilled,
  ClockCircleOutlined,
  CheckCircleOutlined,
  LogoutOutlined,
  CrownOutlined
} from '@ant-design/icons';
import authService from '../services/auth.service';
import useUserStore from '../store/userStore';

const { Header, Content } = AntLayout;

const Layout = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(true);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [avatarError, setAvatarError] = useState(false);
  const notificationsRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Get user from userStore instead of localStorage
  const currentUser = useUserStore((state) => state.user);

  // Reset avatar error when avatar_url or updated_at changes
  useEffect(() => {
    setAvatarError(false);
  }, [currentUser?.avatar_url, currentUser?.updated_at]);

  // Không cần fetch user ở đây vì App.jsx đã fetch rồi

  // Mock notifications data
  const notifications = [
    {
      id: 1,
      title: 'AAPL Breakout Alert',
      message: 'Apple Inc. has broken above resistance at $185.50',
      time: '5 minutes ago',
      read: false,
      type: 'alert'
    },
    {
      id: 2,
      title: 'Portfolio Update',
      message: 'Your portfolio is up 2.3% today',
      time: '1 hour ago',
      read: false,
      type: 'info'
    },
    {
      id: 3,
      title: 'Video Render Complete',
      message: 'CANSLIM Daily Recap — Nov 11 is ready',
      time: '2 hours ago',
      read: true,
      type: 'success'
    },
    {
      id: 4,
      title: 'New CANSLIM Match',
      message: 'TSLA matches 6/7 CANSLIM criteria',
      time: '3 hours ago',
      read: true,
      type: 'alert'
    },
    {
      id: 5,
      title: 'Market Summary',
      message: 'Daily market analysis is available',
      time: '5 hours ago',
      read: true,
      type: 'info'
    },
  ];

  // Apply dark mode to document element
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('tw-dark');
    } else {
      document.documentElement.classList.remove('tw-dark');
    }
  }, [darkMode]);

  // Close notifications dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setNotificationsOpen(false);
      }
    };

    if (notificationsOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [notificationsOpen]);

  // Danh sách các mock pages
  const mockPages = [
    {
      key: 'fintech-mock',
      label: 'Fintech Dashboard',
      icon: <DashboardOutlined />,
      path: '/fintech-mock',
    },
    {
      key: 'portfolio-mock',
      label: 'Portfolio Management',
      icon: <FundOutlined />,
      path: '/portfolio-mock',
    },
    {
      key: 'canslim-analysis',
      label: 'CANSLIM Analysis',
      icon: <BarChartOutlined />,
      path: '/canslim-analysis',
    },
    {
      key: 'canslim-demo',
      label: 'CANSLIM Demo',
      icon: <LineChartOutlined />,
      path: '/canslim-demo',
    },
    {
      key: 'persona-lens',
      label: 'Persona Lens',
      icon: <SearchOutlined />,
      path: '/persona-lens',
    },
    {
      key: 'signals-alerts',
      label: 'Signals & Alerts',
      icon: <BellOutlined />,
      path: '/signals-alerts',
    },
    {
      key: 'search-stock',
      label: 'Search Stock',
      icon: <SearchOutlined />,
      path: '/search-stock',
    },
    {
      key: 'watchlist-notes',
      label: 'Watchlist & Notes',
      icon: <BookOutlined />,
      path: '/watchlist-notes',
    },
    {
      key: 'video-studio',
      label: 'Video Studio',
      icon: <PlaySquareOutlined />,
      path: '/video-studio',
    },
  ];

  const handleMenuClick = ({ key }) => {
    const page = mockPages.find(p => p.key === key);
    if (page) {
      navigate(page.path);
      setDrawerOpen(false);
    }
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  const handleLogout = () => {
    authService.logout();
    navigate('/login');
  };

  const handleUpgrade = () => {
    navigate('/compare-plans');
    setDrawerOpen(false);
  };

  // Get selected key based on current path
  const getSelectedKey = () => {
    const currentPage = mockPages.find(p => p.path === location.pathname);
    return currentPage ? [currentPage.key] : [];
  };

  // Get user initials for avatar
  const getUserInitials = (name) => {
    if (!name) return 'U';

    // If it's an email, use first 2 chars before @
    if (name.includes('@')) {
      return name.substring(0, 2).toUpperCase();
    }

    // If it's a phone, use first 2 digits
    if (/^\d+$/.test(name)) {
      return name.substring(0, 2);
    }

    // If it's a name, use first letter of each word
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Helper function to get avatar URL with cache busting
  const getAvatarUrl = (avatarUrl) => {
    if (!avatarUrl) return null;
    // If it's already a full URL, return it
    if (avatarUrl.startsWith('http')) return avatarUrl;
    // If it's a blob URL (preview), return it directly
    if (avatarUrl.startsWith('blob:')) return avatarUrl;
    
    // Get base URL and remove trailing /api if present
    const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:3000/api').replace(/\/api$/, '');
    
    // If avatar_url already starts with /api, use it directly
    // Otherwise, prepend /api
    const path = avatarUrl.startsWith('/api') ? avatarUrl : `/api${avatarUrl}`;
    
    // Add cache busting parameter based on updated_at or current timestamp
    // This ensures browser reloads the image when avatar is updated
    const cacheBuster = currentUser?.updated_at 
      ? new Date(currentUser.updated_at).getTime() 
      : Date.now();
    
    const separator = path.includes('?') ? '&' : '?';
    return `${baseUrl}${path}${separator}t=${cacheBuster}`;
  };

  // Get package name from subscription
  const getPackageName = () => {
    // Nếu có subscription active, lấy tên từ subscription
    if (currentUser?.subscription?.subscription_name) {
      return currentUser.subscription.subscription_name;
    }
    // Fallback về Free nếu không có subscription
    return 'Free';
  };

  return (
    <AntLayout className="tw-min-h-screen tw-bg-gray-50 dark:tw-bg-fintech-navy tw-transition-colors">
      {/* Header */}
      <Header className="tw-border-b tw-border-gray-200 dark:tw-border-fintech-border tw-bg-white dark:tw-bg-fintech-panel tw-transition-colors tw-sticky tw-top-0 tw-z-[1000] tw-px-6 tw-py-0 tw-flex tw-items-center tw-justify-between tw-h-16">
        {/* Left: Hamburger + Stock Pro */}
        <div className="tw-flex tw-items-center tw-gap-4">
            <MenuOutlined onClick={() => setDrawerOpen(true)} className="tw-text-xl tw-text-gray-900 dark:tw-text-white" />
          <h1 className="tw-text-xl tw-font-semibold tw-tracking-tight tw-text-gray-900 dark:tw-text-white tw-m-0">
            Stock Pro
          </h1>
        </div>

        {/* Right: Notifications + Dark Mode Toggle */}
        <div className="tw-flex tw-items-center tw-gap-3">
          {/* Notification Bell */}
          <div className="tw-relative" ref={notificationsRef}>
            <Badge count={notifications.filter(n => !n.read).length} size="small">
              <button
                onClick={() => setNotificationsOpen(!notificationsOpen)}
                className="tw-p-2 hover:tw-bg-gray-100 dark:hover:tw-bg-fintech-border tw-rounded-lg tw-transition-colors"
              >
                <BellOutlined className="tw-text-xl tw-text-gray-900 dark:tw-text-white" />
              </button>
            </Badge>

            {/* Notifications Dropdown */}
            {notificationsOpen && (
              <div className="tw-absolute tw-right-0 tw-top-full tw-mt-2 tw-w-96 tw-bg-white dark:tw-bg-fintech-panel tw-border tw-border-gray-200 dark:tw-border-fintech-border tw-rounded-lg tw-shadow-xl tw-overflow-hidden tw-z-[1001]">
                {/* Header */}
                <div className="tw-px-4 tw-py-3 tw-border-b tw-border-gray-200 dark:tw-border-fintech-border tw-flex tw-items-center tw-justify-between">
                  <h3 className="tw-text-sm tw-font-bold tw-text-gray-900 dark:tw-text-white">
                    Notifications
                  </h3>
                  <button className="tw-text-xs tw-text-fintech-accent hover:tw-underline">
                    Mark all as read
                  </button>
                </div>

                {/* Notifications List */}
                <div className="tw-max-h-96 tw-overflow-y-auto">
                  {notifications.map((notification) => (
                    <div
                      key={notification.id}
                      className={`tw-px-4 tw-py-3 tw-border-b tw-border-gray-100 dark:tw-border-fintech-border hover:tw-bg-gray-50 dark:hover:tw-bg-fintech-navy tw-cursor-pointer tw-transition-colors ${
                        !notification.read ? 'tw-bg-blue-50/50 dark:tw-bg-blue-500/5' : ''
                      }`}
                    >
                      <div className="tw-flex tw-items-start tw-gap-3">
                        <div className={`tw-w-8 tw-h-8 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 ${
                          notification.type === 'alert' ? 'tw-bg-red-100 dark:tw-bg-red-500/10' :
                          notification.type === 'success' ? 'tw-bg-green-100 dark:tw-bg-green-500/10' :
                          'tw-bg-blue-100 dark:tw-bg-blue-500/10'
                        }`}>
                          {notification.type === 'success' ? (
                            <CheckCircleOutlined className={`tw-text-sm ${
                              notification.type === 'alert' ? 'tw-text-red-600 dark:tw-text-red-400' :
                              notification.type === 'success' ? 'tw-text-green-600 dark:tw-text-green-400' :
                              'tw-text-blue-600 dark:tw-text-blue-400'
                            }`} />
                          ) : (
                            <BellOutlined className={`tw-text-sm ${
                              notification.type === 'alert' ? 'tw-text-red-600 dark:tw-text-red-400' :
                              notification.type === 'success' ? 'tw-text-green-600 dark:tw-text-green-400' :
                              'tw-text-blue-600 dark:tw-text-blue-400'
                            }`} />
                          )}
                        </div>
                        <div className="tw-flex-1 tw-min-w-0">
                          <div className="tw-flex tw-items-start tw-justify-between tw-gap-2 tw-mb-1">
                            <h4 className="tw-text-sm tw-font-semibold tw-text-gray-900 dark:tw-text-white tw-line-clamp-1">
                              {notification.title}
                            </h4>
                            {!notification.read && (
                              <div className="tw-w-2 tw-h-2 tw-bg-blue-500 tw-rounded-full tw-flex-shrink-0 tw-mt-1"></div>
                            )}
                          </div>
                          <p className="tw-text-xs tw-text-gray-600 dark:tw-text-gray-400 tw-line-clamp-2 tw-mb-1">
                            {notification.message}
                          </p>
                          <div className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-gray-500 dark:tw-text-gray-500">
                            <ClockCircleOutlined className="tw-text-xs" />
                            <span>{notification.time}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Footer */}
                <div className="tw-px-4 tw-py-3 tw-border-t tw-border-gray-200 dark:tw-border-fintech-border tw-text-center">
                  <button className="tw-text-sm tw-text-fintech-accent hover:tw-underline tw-font-medium">
                    View all notifications
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Dark Mode Toggle */}
          <button
            onClick={() => setDarkMode(!darkMode)}
            className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-bg-gray-200 dark:tw-bg-fintech-border hover:tw-bg-fintech-accent/10 hover:tw-border-fintech-accent tw-border tw-border-gray-300 dark:tw-border-fintech-border tw-rounded-lg tw-transition-all tw-cursor-pointer tw-text-gray-900 dark:tw-text-white"
          >
            {darkMode ? (
              <span className="tw-flex tw-items-center tw-gap-2">
                <BulbOutlined /> Light
              </span>
            ) : (
              <span className="tw-flex tw-items-center tw-gap-2">
                <BulbFilled /> Dark
              </span>
            )}
          </button>

          {/* Logout Button */}
          <button
            onClick={handleLogout}
            className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-bg-red-500 hover:tw-bg-red-600 tw-border tw-border-red-500 dark:tw-border-red-600 tw-rounded-lg tw-transition-all tw-cursor-pointer tw-text-white"
          >
            <span className="tw-flex tw-items-center tw-gap-2">
              <LogoutOutlined /> Logout
            </span>
          </button>
        </div>
      </Header>

      {/* Drawer Menu */}
      <Drawer
        title={null}
        placement="left"
        onClose={handleDrawerClose}
        open={drawerOpen}
        width={320}
        className={darkMode ? 'tw-dark' : ''}
        styles={{
          body: {
            padding: 0,
            background: darkMode ? '#0f1419' : '#ffffff',
          },
          header: {
            display: 'none',
          },
          wrapper: {
            background: darkMode ? '#0f1419' : '#ffffff',
          }
        }}
      >
        {/* User Info Section */}
        {currentUser && (
          <div className="tw-p-6 tw-border-b tw-border-gray-200 dark:tw-border-fintech-border tw-bg-white dark:tw-bg-fintech-panel">
            <div className="tw-flex tw-items-center tw-gap-3 tw-mb-4">
              {/* Avatar */}
              {currentUser?.avatar_url && 
               typeof currentUser.avatar_url === 'string' && 
               currentUser.avatar_url.trim() !== '' && 
               !avatarError ? (
                <div className="tw-w-14 tw-h-14 tw-rounded-full tw-overflow-hidden tw-flex-shrink-0 tw-shadow-md tw-border-2 tw-border-gray-200 dark:tw-border-fintech-border">
                  <img
                    src={getAvatarUrl(currentUser.avatar_url)}
                    alt="Avatar"
                    className="tw-w-full tw-h-full tw-object-contain"
                    style={{ objectFit: 'contain' }}
                    onError={() => {
                      setAvatarError(true);
                    }}
                    key={`${currentUser.avatar_url}-${currentUser?.updated_at || Date.now()}`} // Force re-render when avatar_url or updated_at changes
                  />
                </div>
              ) : (
                <div className="tw-w-14 tw-h-14 tw-bg-gradient-to-br tw-from-emerald-400 tw-to-emerald-600 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-shadow-md">
                  <span className="tw-text-white tw-font-bold tw-text-xl">
                    {getUserInitials(currentUser.full_name || currentUser.mail || currentUser.phone)}
                  </span>
                </div>
              )}

              {/* User Details */}
              <div className="tw-flex-1 tw-min-w-0">
                <h3 className="tw-text-base tw-font-semibold tw-text-gray-900 dark:tw-text-white tw-truncate tw-mb-1">
                  {currentUser.full_name || currentUser.mail || currentUser.phone}
                </h3>
                <div className="tw-flex tw-items-center tw-gap-1.5">
                  <CrownOutlined className="tw-text-sm tw-text-amber-500" />
                  <span className="tw-text-sm tw-font-medium tw-text-amber-600 dark:tw-text-amber-400">
                    Gói {getPackageName()}
                  </span>
                </div>
              </div>
            </div>

            {/* Upgrade Button */}
            <button
              onClick={handleUpgrade}
              className="tw-w-full tw-py-2.5 tw-px-4 tw-bg-gradient-to-r tw-from-emerald-500 tw-to-emerald-600 hover:tw-from-emerald-600 hover:tw-to-emerald-700 tw-text-white tw-text-sm tw-font-semibold tw-rounded-lg tw-transition-all tw-flex tw-items-center tw-justify-center tw-gap-2 tw-shadow-sm hover:tw-shadow-md"
            >
              <CrownOutlined />
              Nâng cấp gói
            </button>
          </div>
        )}
        <Menu
          mode="inline"
          selectedKeys={getSelectedKey()}
          onClick={handleMenuClick}
          className={darkMode ? 'tw-dark' : ''}
          style={{
            border: 'none',
            background: darkMode ? '#0f1419' : '#ffffff',
          }}
          items={mockPages.map(page => ({
            key: page.key,
            icon: page.icon,
            label: page.label,
            style: {
              fontSize: '15px',
              height: '48px',
              display: 'flex',
              alignItems: 'center',
              color: darkMode ? '#e5e7eb' : '#1f2937',
              background: darkMode ? '#0f1419' : '#ffffff',
            }
          }))}
        />
      </Drawer>

      {/* Content */}
      <Content className="tw-bg-gray-50 dark:tw-bg-fintech-navy tw-transition-colors">
        <Outlet />
      </Content>
    </AntLayout>
  );
};

export default Layout;
